// react
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from 'router';

// store
import { apiUrl } from 'store/api';
import { useSelector } from 'store';
import { getAccount } from 'store/slices/iamSlice';

// hooks
import { useAuth, useApp, useCareCircle, useOnboarding } from 'hooks';

// components
import { AppStore } from '@karehero/llama/components/organisms/AppStore/AppStore';
import { Dialog } from '@karehero/llama/components/atoms/Dialog/Dialog';

import LoadingPage from '@karehero/llama/components/pages/Loading/Loading';
import OnboardingFlow from '@karehero/llama/components/pages/OnboardingFlow/OnboardingFlow';

// posthog
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import * as Sentry from '@sentry/react';

// models
import {
  MemberRole,
  QuestionID,
  QuestionResponse,
  Role,
} from '@karehero/models';
import { useHandleTriageResponsesMutation } from 'store/api/triage';
import { invalidateNotificationTags } from 'store/api/notification';
import { dispatch } from 'store';
import { newToast } from 'store/slices/uiSlice';
import Button from '@karehero/llama/components/atoms/Button/Button';
import Typography from '@mui/material/Typography';
import { Container, Stack } from '@mui/material';

const CareCircleLoadTimeoutPage = () => {
  return (
    <Container maxWidth='sm'>
      <Stack spacing={4}>
        <Typography variant='body1' align='center'>
          Error
        </Typography>
        <Typography variant='body2' align='center'>
          An error occurred while loading your care circle. Please reload the
          page using the button below.
        </Typography>
        <Button
          onClick={() => window.location.reload()}
          ariaLabel={'Reload'}
          variant='secondary'
          size='md'
        >
          Reload
        </Button>
      </Stack>
    </Container>
  );
};

/**
 * OnboardingMemberCreate takes the user through the onboarding flow for a care concierge.
 */
export const OnboardingMemberCreate = () => {
  // hooks
  const { flags } = useFeatureFlags();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const { isApp } = useApp();
  const { isAuthenticated, isLoading } = useAuth();
  const account = useSelector(getAccount);
  const { careCircleMember, careCircle } = useCareCircle();
  const [handleTriageResponses] = useHandleTriageResponsesMutation();

  // careCircleMember from useCareCircle is always the primary caregiver,
  // so we find the logged-in user's own member record from the care circle.
  const currentUserMember = useMemo(
    () =>
      careCircle?.careCircleMembers?.find((m) => m.account.id === account.id),
    [careCircle, account.id],
  );

  const isPrimaryCaregiver = useMemo(
    () =>
      currentUserMember?.roles.some(
        (r: Role) => r.name === MemberRole.PrimaryCaregiver,
      ) ?? true,
    [currentUserMember],
  );

  const { typeformConfig, onboardingQuestions, updateIsOnboarded, responses } =
    useOnboarding({
      typeformID: account?.organization?.typeformID,
      isPrimaryCaregiver,
    });

  // state
  const [isLoadingFailed, setIsLoadingFailed] = useState(false);
  const [isAppStoreOpen, setIsAppStoreOpen] = useState(!isApp);
  const [isTypeformSkip, setIsTypeformSkip] = useState(false);

  // methods
  const handleNext = useCallback(
    (configID: string, subConfigID: string) => {
      setSearchParams({ configID, subConfigID });
    },
    [setSearchParams],
  );

  const handleComplete = useCallback(async () => {
    if (isPrimaryCaregiver) {
      const structuredResponses: QuestionResponse[] = (
        Object.entries(responses) as [QuestionID, string | undefined][]
      )
        .filter(
          ([_, response]) =>
            response !== undefined &&
            response !== '' &&
            response !== null &&
            response !== '[]',
        )
        .map(([questionID, response]) => ({
          questionID,
          response: response as string,
        }));

      if (!careCircle?.id) {
        console.error('Care circle ID not found');
        dispatch(
          newToast({
            title: 'Error',
            description:
              'An error occurred while handling your responses. Please try again.',
            variant: 'error',
          }),
        );
        Sentry.captureException(new Error('Care circle ID not found'));
        return navigate(ROUTES.HOME);
      }

      const res = await handleTriageResponses({
        responses: structuredResponses,
        careCircleID: careCircle?.id,
      });

      if ('error' in res) {
        console.error(res.error);
        return;
      }
    }

    await updateIsOnboarded();
    dispatch(invalidateNotificationTags());
    navigate(ROUTES.HOME);
  }, [
    updateIsOnboarded,
    navigate,
    responses,
    handleTriageResponses,
    isPrimaryCaregiver,
    careCircle,
  ]);

  // memos
  const onboardingConfig = useMemo(
    () =>
      isTypeformSkip
        ? []
        : flags.onboardingTypeform &&
            account?.organization?.typeformID &&
            isPrimaryCaregiver
          ? typeformConfig
          : onboardingQuestions,
    [
      typeformConfig,
      onboardingQuestions,
      flags,
      isTypeformSkip,
      isPrimaryCaregiver,
      account,
    ],
  );

  // effects
  useEffect(() => {
    if (!careCircleMember && !isLoadingFailed) {
      const timeoutId = setTimeout(() => {
        setIsLoadingFailed(true);
      }, 5000);
      return () => clearTimeout(timeoutId);
    }
  }, [careCircleMember, isLoadingFailed]);

  useEffect(() => {
    if (searchParams.get('utm_source') === 'karehero_typeform') {
      setIsTypeformSkip(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isAuthenticated && !isLoading) navigate(ROUTES.SIGN_IN);
  }, [navigate, isAuthenticated, isLoading]);

  // Non-primary caregivers have no onboarding steps — complete immediately.
  useEffect(() => {
    if (onboardingConfig.length === 0) {
      handleComplete();
    }
  }, [onboardingConfig, handleComplete]);

  // early returns (all hooks must be above this point)
  if (
    !isAuthenticated ||
    !careCircleMember ||
    !careCircleMember?.id ||
    isLoading
  ) {
    return <LoadingPage />;
  }

  if (isLoadingFailed) {
    Sentry.captureException(
      new Error(
        'Error loading care circle: the user is not authenticated or the care circle is not loaded after 5 seconds',
      ),
    );

    dispatch(
      newToast({
        title: 'Error',
        description:
          'An error occurred while loading your care circle. Please reload the page.',
        variant: 'error',
      }),
    );

    return <CareCircleLoadTimeoutPage />;
  }

  if (onboardingConfig.length === 0) {
    return <LoadingPage />;
  }

  return (
    <>
      <OnboardingFlow
        config={onboardingConfig}
        onNext={handleNext}
        onComplete={handleComplete}
      />
      {flags.appStoreSignpost && (
        <Dialog
          variant='full'
          isOpen={isAppStoreOpen}
          setIsOpen={setIsAppStoreOpen}
        >
          <AppStore storeUrl={`${apiUrl}/v1/app-store`} />
        </Dialog>
      )}
    </>
  );
};

OnboardingMemberCreate.displayName = 'OnboardingMemberCreate';
export default OnboardingMemberCreate;
